import { useState, useEffect, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { authService } from "../../services";
import { useBranding } from "../../context/BrandingContext";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type Step = "EMAIL" | "OTP_PASSWORD" | "SUCCESS";

export default function ForgotPasswordPage() {
  const { businessName, tagline, effectiveLogo } = useBranding();
  const [step, setStep] = useState<Step>("EMAIL");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [error, setError] = useState<string | null>(null);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  // Timer countdown for resending OTP
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => {
      setResendCooldown((prev) => prev - 1);
    }, 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  // Step 1: Request OTP
  const handleSendOtp = async (e?: FormEvent) => {
    if (e) e.preventDefault();
    if (submitting) return;

    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setError("Please enter your email address.");
      return;
    }
    if (!EMAIL_RE.test(trimmedEmail)) {
      setError("Enter a valid email address.");
      return;
    }

    setError(null);
    setInfoMessage(null);
    setSubmitting(true);

    try {
      const res = await authService.forgotPassword(trimmedEmail);
      setInfoMessage(res.message || "A 6-digit OTP has been sent to your email.");
      setStep("OTP_PASSWORD");
      setResendCooldown(60); // 60s cooldown for resend
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to send OTP. Please check your email and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // Resend OTP
  const handleResendOtp = async () => {
    if (resendCooldown > 0 || submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      const res = await authService.forgotPassword(email.trim());
      setInfoMessage(res.message || "A fresh OTP has been sent to your email.");
      setResendCooldown(60);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to resend OTP.");
    } finally {
      setSubmitting(false);
    }
  };

  // Step 2: Verify OTP & Change Password
  const handleResetPassword = async (e: FormEvent) => {
    e.preventDefault();
    if (submitting) return;

    const cleanOtp = otp.trim();
    if (!cleanOtp || cleanOtp.length < 4) {
      setError("Please enter the complete OTP code.");
      return;
    }
    if (!newPassword) {
      setError("Please enter your new password.");
      return;
    }
    if (newPassword.length < 6) {
      setError("New password must be at least 6 characters long.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match. Please re-enter.");
      return;
    }

    setError(null);
    setSubmitting(true);

    try {
      await authService.resetPassword(email.trim(), cleanOtp, newPassword);
      setStep("SUCCESS");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to reset password. Please check the OTP.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background-50 px-4 py-10">
      <div className="w-full max-w-md">
        {/* Brand Header */}
        <div className="mb-8 text-center flex flex-col items-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-xl bg-white border border-background-200 p-1.5 shadow-sm overflow-hidden">
            <img src={effectiveLogo} alt={businessName} className="h-full w-full object-contain" />
          </span>
          <h1 className="mt-4 text-xl font-semibold text-foreground-950">{businessName}</h1>
          <p className="mt-0.5 text-xs text-foreground-500">{tagline}</p>
        </div>

        {/* Main Card */}
        <div className="rounded-xl border border-background-200 bg-white p-6 shadow-sm sm:p-8">
          {/* Error Notification */}
          {error ? (
            <div className="mb-5 flex items-start gap-2.5 rounded-lg border border-danger-200 bg-danger-50 px-3.5 py-3 text-sm text-danger-700 animate-fadeIn">
              <i className="ri-close-circle-line mt-0.5 flex-none text-base" />
              <span className="leading-snug">{error}</span>
            </div>
          ) : null}

          {/* Info Notification */}
          {infoMessage && step !== "SUCCESS" ? (
            <div className="mb-5 flex items-start gap-2.5 rounded-lg border border-primary-200 bg-primary-50 px-3.5 py-3 text-sm text-primary-800 animate-fadeIn">
              <i className="ri-mail-send-line mt-0.5 flex-none text-base text-primary-600" />
              <span className="leading-snug">{infoMessage}</span>
            </div>
          ) : null}

          {/* STEP 1: Enter Email to Receive OTP */}
          {step === "EMAIL" && (
            <>
              <div className="mb-5">
                <h2 className="text-lg font-semibold text-foreground-900">Forgot your password?</h2>
                <p className="mt-1 text-sm text-foreground-500">
                  Enter your registered account email and we'll send a 6-digit OTP to reset your password.
                </p>
              </div>

              <form onSubmit={handleSendOtp} className="space-y-4" noValidate>
                <div className="space-y-1.5">
                  <label htmlFor="email" className="block text-xs font-semibold text-foreground-700">
                    Email address
                  </label>
                  <div className="relative">
                    <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-foreground-400">
                      <i className="ri-mail-line text-base" />
                    </span>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      autoFocus
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="name@example.com"
                      className="h-10 w-full rounded-lg border border-background-300 bg-white pl-9 pr-3 text-sm transition-colors focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="inline-flex h-10 w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-primary-600 text-sm font-medium text-white shadow transition-all hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500/50 disabled:cursor-not-allowed disabled:bg-primary-300"
                >
                  {submitting ? <i className="ri-loader-4-line animate-spin text-base" /> : <i className="ri-mail-send-line text-base" />}
                  {submitting ? "Sending OTP…" : "Send OTP to Email"}
                </button>
              </form>

              <p className="mt-6 text-center text-sm">
                <Link to="/login" className="inline-flex items-center gap-1.5 font-medium text-primary-600 hover:text-primary-700 hover:underline">
                  <i className="ri-arrow-left-line" /> Back to sign in
                </Link>
              </p>
            </>
          )}

          {/* STEP 2: Verify OTP and Set New Password */}
          {step === "OTP_PASSWORD" && (
            <>
              <div className="mb-5">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-foreground-900">Verify OTP & Set Password</h2>
                  <button
                    type="button"
                    onClick={() => {
                      setStep("EMAIL");
                      setError(null);
                      setInfoMessage(null);
                    }}
                    className="text-xs font-medium text-primary-600 hover:underline"
                  >
                    Change email
                  </button>
                </div>
                <p className="mt-1 text-xs text-foreground-500">
                  Enter the 6-digit code sent to <span className="font-semibold text-foreground-800">{email}</span> and your new password.
                </p>
              </div>

              <form onSubmit={handleResetPassword} className="space-y-4" noValidate>
                {/* OTP Input */}
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <label htmlFor="otp" className="block text-xs font-semibold text-foreground-700">
                      6-Digit OTP Code
                    </label>
                    <button
                      type="button"
                      disabled={resendCooldown > 0 || submitting}
                      onClick={handleResendOtp}
                      className="text-xs font-medium text-primary-600 hover:underline disabled:cursor-not-allowed disabled:text-foreground-400"
                    >
                      {resendCooldown > 0 ? `Resend OTP in ${resendCooldown}s` : "Resend OTP"}
                    </button>
                  </div>
                  <input
                    id="otp"
                    name="otp"
                    type="text"
                    inputMode="numeric"
                    maxLength={8}
                    autoFocus
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                    placeholder="Enter 6-digit OTP"
                    className="h-11 w-full text-center tracking-[0.3em] font-mono text-lg font-bold rounded-lg border border-background-300 bg-white px-3 transition-colors focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
                  />
                </div>

                {/* New Password */}
                <div className="space-y-1.5">
                  <label htmlFor="newPassword" className="block text-xs font-semibold text-foreground-700">
                    New Password
                  </label>
                  <div className="relative">
                    <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-foreground-400">
                      <i className="ri-lock-line text-base" />
                    </span>
                    <input
                      id="newPassword"
                      name="newPassword"
                      type={showNewPassword ? "text" : "password"}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="At least 6 characters"
                      className="h-10 w-full rounded-lg border border-background-300 bg-white pl-9 pr-10 text-sm transition-colors focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute inset-y-0 right-0 flex items-center pr-3 text-foreground-400 hover:text-foreground-600"
                    >
                      <i className={showNewPassword ? "ri-eye-off-line" : "ri-eye-line"} />
                    </button>
                  </div>
                </div>

                {/* Confirm Password */}
                <div className="space-y-1.5">
                  <label htmlFor="confirmPassword" className="block text-xs font-semibold text-foreground-700">
                    Confirm New Password
                  </label>
                  <div className="relative">
                    <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-foreground-400">
                      <i className="ri-lock-check-line text-base" />
                    </span>
                    <input
                      id="confirmPassword"
                      name="confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Re-enter new password"
                      className="h-10 w-full rounded-lg border border-background-300 bg-white pl-9 pr-10 text-sm transition-colors focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute inset-y-0 right-0 flex items-center pr-3 text-foreground-400 hover:text-foreground-600"
                    >
                      <i className={showConfirmPassword ? "ri-eye-off-line" : "ri-eye-line"} />
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="inline-flex h-10 w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-primary-600 text-sm font-medium text-white shadow transition-all hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500/50 disabled:cursor-not-allowed disabled:bg-primary-300"
                >
                  {submitting ? <i className="ri-loader-4-line animate-spin text-base" /> : <i className="ri-check-line text-base" />}
                  {submitting ? "Resetting Password…" : "Verify OTP & Change Password"}
                </button>
              </form>

              <p className="mt-6 text-center text-sm">
                <Link to="/login" className="inline-flex items-center gap-1.5 font-medium text-primary-600 hover:text-primary-700 hover:underline">
                  <i className="ri-arrow-left-line" /> Cancel and back to sign in
                </Link>
              </p>
            </>
          )}

          {/* STEP 3: Success Confirmation */}
          {step === "SUCCESS" && (
            <div className="py-4 text-center">
              <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-success-100 text-success-600 shadow-sm">
                <i className="ri-checkbox-circle-fill text-3xl" />
              </span>
              <h2 className="mt-4 text-lg font-bold text-foreground-900">Password Changed Successfully!</h2>
              <p className="mt-2 text-sm text-foreground-600">
                Your password has been updated. You can now sign in to your account with your new credentials.
              </p>
              <Link
                to="/login"
                className="mt-6 inline-flex h-10 w-full items-center justify-center gap-2 rounded-lg bg-primary-600 px-4 text-sm font-medium text-white shadow hover:bg-primary-700 transition-colors"
              >
                <i className="ri-login-box-line text-base" /> Proceed to Sign In
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}